import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import type {
  CourseLevel,
  CourseColorTheme,
} from '@/pages/home/aboutLc.content'

interface CourseLevelCardsProps {
  titleHigh: string
  titlePrimary: string
  titleHighMobileA: string
  titleHighMobileB: string
  titlePrimaryMobileA: string
  titlePrimaryMobileB: string
  descPart1: string
  descEmphasis: string
  descPart2: string
  levels: CourseLevel[]
}

const COLOR_MAP: Record<
  CourseColorTheme,
  { bg: string; tagBg: string; tagText: string }
> = {
  green: { bg: '#48ca6d', tagBg: '#d2f3e3', tagText: '#2ac47e' },
  yellow: { bg: '#fa0', tagBg: '#ffeec8', tagText: '#fa0' },
  blue: { bg: '#4599ff', tagBg: '#dceaff', tagText: '#4599ff' },
  purple: { bg: '#c58ffe', tagBg: '#f0e2ff', tagText: '#b46dff' },
  red: { bg: '#ff7f77', tagBg: '#fddcdb', tagText: '#ff675e' },
}

function TextCard({
  level,
  rounded = '1.5em',
}: {
  level: CourseLevel
  rounded?: string
}) {
  const { t: mt } = useTranslation('marketing')

  const c = COLOR_MAP[level.colorTheme]
  return (
    <div
      className="course-card-details absolute bottom-[0.5em] left-[0.5em] right-[0.5em] bg-white p-[1.25em] flex flex-col gap-[1em]"
      style={{ borderRadius: rounded }}
    >
      <div className="flex items-center justify-between w-full">
        <span
          className="inline-flex items-center px-[0.625em] py-[0.125em] rounded-full text-[1em] leading-[1.5] font-medium"
          style={{ backgroundColor: c.tagBg, color: c.tagText }}
        >
          {mt('course.lessons')}
        </span>
        <span className="text-[1.125em] leading-[1.5] font-medium text-qb-text-mid">
          {level.lessons}
        </span>
      </div>
      <div className="flex items-center justify-between w-full">
        <span
          className="inline-flex items-center px-[0.625em] py-[0.125em] rounded-full text-[1em] leading-[1.5] font-medium"
          style={{ backgroundColor: c.tagBg, color: c.tagText }}
        >
          {mt('course.duration')}
        </span>
        <span className="text-[1.125em] leading-[1.5] font-medium text-qb-text-mid">
          {level.duration}
        </span>
      </div>
    </div>
  )
}

function DesktopCard({ level }: { level: CourseLevel }) {
  const { t: mt } = useTranslation('marketing')

  const c = COLOR_MAP[level.colorTheme]
  return (
    <article
      className="course-desktop relative h-[21.375em] w-[15.25em] rounded-[2em] overflow-hidden shadow-[0px_8px_16px_0px_rgba(0,0,0,0.2)] shrink-0"
      style={{
        backgroundColor: c.bg,
        fontSize: 'clamp(11px, calc(1.2vw - 1px), 16px)',
      }}
    >
      {/* Pattern — centered horizontally, fixed top spacing */}
      <img
        src={level.patternUrl}
        alt=""
        aria-hidden
        className="absolute top-[2.5em] left-1/2 -translate-x-1/2 max-w-[14.25em] h-auto pointer-events-none"
      />

      {/* Course letter + 과정 */}
      <div className="course-card-heading absolute top-[1.0625em] left-1/2 -translate-x-1/2 flex flex-col items-center text-white text-center">
        <span className="font-bold text-[3.5em] leading-[1.2] tracking-[-0.0375em]">
          {level.course}
        </span>
        <span className="font-bold text-[1.25em] leading-[1.5]">{mt('course.pathway')}
        </span>
      </div>

      {/* Title */}
      <div className="course-card-copy absolute top-[7.625em] left-1/2 -translate-x-1/2 w-full text-center font-semibold text-white tracking-[-0.025em] whitespace-nowrap">
        <p className="text-[1.5em] leading-[1.4]">{level.titleA}</p>
        <p className="text-[1.5em] leading-[1.4]">{level.titleB}</p>
      </div>

      {/* White text card */}
      <TextCard level={level} rounded="1.5em" />
    </article>
  )
}

function MobileCard({ level }: { level: CourseLevel }) {
  const { t: mt } = useTranslation('marketing')

  const c = COLOR_MAP[level.colorTheme]
  return (
    <article
      className="course-mobile relative h-[240px] w-full rounded-[24px] overflow-hidden shadow-[0px_8px_16px_0px_rgba(0,0,0,0.2)]"
      style={{ backgroundColor: c.bg }}
    >
      {/* Pattern — centered with fixed top spacing */}
      <img
        src={level.patternUrl}
        alt=""
        aria-hidden
        className="absolute top-[16px] right-[16px] max-h-[118px] w-auto pointer-events-none"
      />

      {/* Title (left) + course letter (right) at top */}
      <div className="course-mobile-heading absolute top-[20px] left-6 right-6 flex items-start justify-between">
        <div className="font-semibold text-white text-[20px] leading-[1.5] tracking-[-0.6px]">
          <p>{level.titleA}</p>
          <p>{level.titleB}</p>
        </div>
        <div className="course-mobile-marker flex flex-col items-center text-white text-center w-[41px] shrink-0">
          <span className="font-bold text-[48px] leading-[1.2] tracking-[-0.6px]">
            {level.course}
          </span>
          <span className="font-bold text-[18px] leading-[1.5]">{mt('course.pathway')}
          </span>
        </div>
      </div>

      {/* White text card */}
      <TextCard level={level} rounded="16px" />
    </article>
  )
}

export function CourseLevelCards({
  titleHigh,
  titlePrimary,
  titleHighMobileA,
  titleHighMobileB,
  titlePrimaryMobileA,
  titlePrimaryMobileB,
  descPart1,
  descEmphasis,
  descPart2,
  levels,
}: CourseLevelCardsProps) {
  const [activeMobile, setActiveMobile] = useState(levels[0]?.course ?? '')
  const currentMobile =
    levels.find((l) => l.course === activeMobile) ?? levels[0]

  return (
    <section id="courses" data-marketing-section="" className="w-full bg-qb-bg-tertiary py-14 lg:py-24 tracking-[-0.6px] overflow-x-clip">
      <div className="mx-auto max-w-[1300px] px-6 lg:px-10 flex flex-col gap-14 lg:gap-20 items-center">
        {/* Header */}
        <div className="flex flex-col items-center gap-6 lg:gap-10 text-center">
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
            <span className="block text-qb-text-high">
              <span className="hidden lg:inline">{titleHigh}</span>
              <span className="lg:hidden">
                {titleHighMobileA}
                <br aria-hidden />
                {titleHighMobileB}
              </span>
            </span>
            <span className="block mt-1 lg:mt-2.5 text-qb-primary">
              <span className="hidden lg:inline">{titlePrimary}</span>
              <span className="lg:hidden">
                {titlePrimaryMobileA}
                <br aria-hidden />
                {titlePrimaryMobileB}
              </span>
            </span>
          </h2>
          <p className="font-normal text-qb-text-mid text-[20px] lg:text-[24px] leading-[1.5] lg:leading-[1.4] whitespace-pre-line">
            {descPart1}
            <span className="font-semibold text-qb-text-high">
              {descEmphasis}
            </span>
            {descPart2}
          </p>
        </div>

        {/* Desktop — 5 cards in a row */}
        <div className="hidden lg:flex gap-4 items-center justify-center">
          {levels.map((level) => (
            <DesktopCard key={level.course} level={level} />
          ))}
        </div>

        {/* Tablet — 3 + 2 grid */}
        <div className="hidden md:flex lg:hidden flex-col gap-4 items-center">
          <div className="flex gap-4 justify-center">
            {levels.slice(0, 3).map((level) => (
              <DesktopCard key={level.course} level={level} />
            ))}
          </div>
          <div className="flex gap-4 justify-center">
            {levels.slice(3).map((level) => (
              <DesktopCard key={level.course} level={level} />
            ))}
          </div>
        </div>

        {/* Mobile — tab strip + 1 card */}
        <div className="md:hidden w-full max-w-[360px] mx-auto flex flex-col gap-6">
          <div role="tablist" className="flex items-center justify-between">
            {levels.map((level) => {
              const c = COLOR_MAP[level.colorTheme]
              const isActive = level.course === activeMobile
              return (
                <button
                  key={level.course}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveMobile(level.course)}
                  className="flex items-center justify-center w-14 h-14 rounded-[16px] font-semibold text-[20px] tracking-[-0.6px] transition-opacity"
                  style={{
                    backgroundColor: isActive ? c.bg : '#dceaff',
                    color: isActive ? '#ffffff' : c.bg,
                  }}
                >
                  {level.course}
                </button>
              )
            })}
          </div>
          {currentMobile && <MobileCard level={currentMobile} />}
        </div>
      </div>
    </section>
  )
}
